// Path-based action required service

import { MessageFnType } from '@components/Component/Exams/ExamScoreEventEmitter';
import EventEmitter from '@utils/eventEmitter';
import { useEffect, useRef, useState } from 'react';
import ActionStore from './actionStore';

/**
 * @typedef {string} ActionPath - A path to an action in this format: [key,id,...id].
 */
export type ActionPath = Array<string | number>;

export interface ActionDecisionMethod<T extends Record<string, any>> {
  name: string;
  method: (context: T) => ActionPath[] | null;
}

export const setValueOnNestedObject = (
  obj: Record<string, any>,
  path: ActionPath,
  value: boolean,
) => {
  if (path.length === 0) {
    return;
  }
  if (path.length === 1) {
    // eslint-disable-next-line no-param-reassign
    obj[path[0]] = value;
    return;
  }
  if (typeof obj[path[0]] !== 'object' || Array.isArray(obj[path[0]])) {
    // eslint-disable-next-line no-param-reassign
    obj[path[0]] = {};
  }
  setValueOnNestedObject(obj[path[0]], path.slice(1), value);
};
// it will only return paths to leaf nodes, not intermediate nodes
export const buildPathsForDeletedObject = (
  obj: Record<string, any>,
  path: ActionPath,
) => {
  const paths: ActionPath[] = [];
  Object.keys(obj).forEach((key) => {
    const newPath = [...path, key];
    const value = obj[key];
    // Only add the path if it's a leaf node (not an object or is null/undefined)
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      paths.push(newPath);
    } else {
      // If it's an object, recurse to find leaf paths
      paths.push(...buildPathsForDeletedObject(value, newPath));
    }
  });
  return paths;
};

export function deepReconciliation(oldObj: any, newObj: any) {
  const changes = {
    added: [] as ActionPath[],
    removed: [] as ActionPath[],
  };

  function compareObjects(base: any, compare: any, path: ActionPath = []) {
    // Check for added properties in compare object
    Object.keys(compare).forEach((key) => {
      const currentPath = [...path, key];

      if (!(key in base)) {
        // If it's a leaf node (boolean value), add it
        if (typeof compare[key] === 'boolean') {
          changes.added.push(currentPath);
        } else if (typeof compare[key] === 'object' && compare[key] !== null) {
          // If it's an object, recurse to find all leaf nodes
          compareObjects({}, compare[key], currentPath);
        }
      } else if (
        typeof compare[key] === 'object' &&
        compare[key] !== null &&
        typeof base[key] === 'object' &&
        base[key] !== null
      ) {
        // If both values are objects, recurse
        compareObjects(base[key], compare[key], currentPath);
      }
    });

    // Check for removed properties in base object
    Object.keys(base).forEach((key) => {
      const currentPath = [...path, key];

      if (!(key in compare)) {
        if (typeof base[key] === 'object' && base[key] !== null) {
          changes.removed.push(...buildPathsForDeletedObject(base[key], currentPath));
        } else {
          changes.removed.push(currentPath);
        }
      }
    });
  }

  compareObjects(oldObj, newObj);
  return changes;
}

export const EVENTS = {
  ACTION_CHANGED: 'actionChanged',
  UPDATE_ALL_ACTIONS: 'updateAllActions',
} as const;

export type ActionChangedEvent = {
  [EVENTS.ACTION_CHANGED]: { path: ActionPath; value: boolean };
  [EVENTS.UPDATE_ALL_ACTIONS]: undefined;
};

export interface IActionRequiredService<T extends Record<string, any>> {
  // Core service methods
  registerDecisionMethod: (method: ActionDecisionMethod<T>) => void;
  unregisterDecisionMethod: (name: string) => void;
  updateActions: (context: T) => void;

  // Action checking methods
  hasAction: (path: ActionPath) => boolean;

  // Action management
  clearAllActions: () => void;

  // Get all decision methods
  getDecisionMethods: () => ActionDecisionMethod<T>[];

  // Event emitter for action changes
  eventEmitter: EventEmitter<ActionChangedEvent>;
}

export default class ActionRequiredService<T extends Record<string, any>>
  implements IActionRequiredService<T>
{
  private decisionMethods: ActionDecisionMethod<T>[] = [];

  private store: ActionStore;

  public eventEmitter = new EventEmitter<ActionChangedEvent>();

  constructor() {
    this.store = new ActionStore(this.eventEmitter);
  }

  // Centralized store update method using existing methods
  private updateStore(newStore: Record<string, any>) {
    const oldStore = { ...this.store.getStore() };
    const changes = deepReconciliation(oldStore, newStore);
    this.applyStoreChanges(changes);
  }

  // Apply changes using setAction and clearAction methods
  private applyStoreChanges(changes: ReturnType<typeof deepReconciliation>) {
    // Clear removed actions
    changes.removed.forEach((path) => {
      this.store.setAction(path, false);
    });

    // Set added/updated actions
    [...changes.added].forEach((path) => {
      this.store.setAction(path, true);
    });
  }

  registerDecisionMethod(method: ActionDecisionMethod<T>) {
    this.decisionMethods.push(method);
  }

  unregisterDecisionMethod(name: string) {
    this.decisionMethods = this.decisionMethods.filter((m) => m.name !== name);
  }

  updateActions(context: T) {
    const newStore: Record<string, any> = {};

    // Build new store from decision methods
    this.decisionMethods.forEach(({ method }) => {
      const actionPaths = method(context);
      if (actionPaths && actionPaths.length > 0) {
        actionPaths.forEach((actionPath) => {
          setValueOnNestedObject(newStore, actionPath, true);
        });
      }
    });

    // Use centralized store update
    this.updateStore(newStore);
  }

  private getAction(path: ActionPath, object: Record<string, any>): boolean {
    if (path.length === 0) {
      return false;
    }
    if (path.length === 1) {
      // Check if the key exists and has a truthy value
      return !!object?.[path[0]];
    }
    // If the intermediate path doesn't exist, return false
    if (!object?.[path[0]]) {
      return false;
    }
    return this.getAction(path.slice(1), object[path[0]]);
  }

  hasAction(path: ActionPath): boolean {
    return this.getAction(path, this.store.getStore());
  }

  clearAllActions() {
    // Use centralized store update with empty store
    this.updateStore({});
  }

  getDecisionMethods() {
    return this.decisionMethods;
  }

  // Test helper method to set up store for unit testing
  __testSetStore(store: Record<string, any>) {
    this.store = new ActionStore(this.eventEmitter, store);
  }

  // Test helper method to get store state for unit testing
  __testGetStore(): Record<string, any> {
    return this.store.getStore();
  }
}

// Custom hook for React components to use the service
export const useHasAction = <T extends Record<string, any>>(
  service: IActionRequiredService<T>,
  path: ActionPath,
): boolean => {
  const pathRef = useRef(path);
  const [value, setValue] = useState(service.hasAction(path));

  useEffect(() => {
    if (pathRef.current !== path) {
      pathRef.current = path;
      setValue(service.hasAction(path));
    }
    const handler: MessageFnType = (
      data: ActionChangedEvent[typeof EVENTS.ACTION_CHANGED],
    ) => {
      if (data.path.join('.') !== path.join('.')) {
        return;
      }
      const newValue = service.hasAction(path);
      if (newValue !== value) {
        setValue(newValue);
      }
    };

    service.eventEmitter.on(EVENTS.ACTION_CHANGED, handler);

    return () => {
      service.eventEmitter.off(EVENTS.ACTION_CHANGED, handler);
    };
  }, [service, path, pathRef, value]);

  return value;
};

// Helper to create custom decision method
export const createCustomDecisionMethod = <T extends Record<string, any>>(
  name: string,
  method: (context: T) => ActionPath[],
): ActionDecisionMethod<T> => ({
  name,
  method,
});
